import { cn } from '@noorden-insurtech/alba-design-system';
import {
  CircleCheck,
  ClipboardList,
  Clock,
  FileX2,
  LayoutList,
  Send,
  type LucideIcon,
} from 'lucide-react';
import {
  PROPOSAL_STATUS_GROUPS,
  groupStatusValue,
} from '../proposal-status-groups';

type StatusCardConfig = {
  status: string;
  label: string;
  icon: LucideIcon;
};

const GROUP_ICONS: Record<string, LucideIcon> = {
  filling: ClipboardList,
  analysis: Clock,
  accepted: CircleCheck,
  transmitted: Send,
  cancelled: FileX2,
};

const STATUS_CARDS: StatusCardConfig[] = [
  {
    status: '',
    label: 'Total de Propostas',
    icon: LayoutList,
  },
  ...PROPOSAL_STATUS_GROUPS.map(group => ({
    status: groupStatusValue(group),
    label: group.label,
    icon: GROUP_ICONS[group.key] ?? LayoutList,
  })),
];

type ProposalStatusCardsProps = {
  counts: Array<{ status: string; total: number }>;
  activeStatus: string | null;
  onSelectStatus: (status: string | null) => void;
  isLoading?: boolean;
};

export function ProposalStatusCards({
  counts,
  activeStatus,
  onSelectStatus,
  isLoading,
}: ProposalStatusCardsProps) {
  const getCount = (status: string) =>
    counts.find(c => c.status === status)?.total ?? 0;

  return (
    <div className='grid w-full grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6 lg:gap-4'>
      {STATUS_CARDS.map(card => {
        const isSelected =
          card.status === ''
            ? activeStatus === null
            : activeStatus === card.status;
        const Icon = card.icon;

        return (
          <button
            key={card.status || 'total'}
            type='button'
            onClick={() => {
              if (card.status === '') {
                onSelectStatus(null);
              } else {
                onSelectStatus(isSelected ? null : card.status);
              }
            }}
            className={cn(
              'flex min-h-[102px] cursor-pointer flex-col rounded-2xl border px-3 py-3 text-left transition-all lg:min-h-[132px] lg:px-4',
              isSelected
                ? 'border-2 border-alba-green-600 bg-white shadow-sm'
                : 'border-alba-gray-200 bg-white hover:border-alba-gray-300 hover:shadow-sm'
            )}
          >
            <div className='flex items-start justify-between gap-3'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-alba-green-50 lg:h-9 lg:w-9'>
                <Icon
                  className='h-5 w-5 text-alba-green-600'
                  strokeWidth={1.6}
                />
              </div>
              <div className='flex min-h-9 items-center justify-end'>
                <span className='font-montserrat text-3xl font-bold leading-none text-alba-black-500 tabular-nums lg:text-2xl'>
                  {isLoading ? '—' : getCount(card.status)}
                </span>
              </div>
            </div>
            <span className='mt-auto font-montserrat text-xs leading-tight text-alba-gray-600 lg:text-sm'>
              {card.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
